import React, { useState, useEffect } from "react"
import { get, post, remove } from "../../utils/apiClient"
import toast from "react-hot-toast"
import moment from "moment-timezone"

const MonitoringApplicationConfig = () => {
  const [appConfigs, setAppConfigs] = useState([])
  const [showPopup, setShowPopup] = useState(false)
  const [appIdToDelete, setAppIdToDelete] = useState(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [apps, setApps] = useState([])
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeRow, setActiveRow] = useState(null)

  const fetchApps = async () => {
    //setAppLoading(true)
    try {
      const data = await get("/admin/applications")

      setApps(data)
      //setAppLoading(false)
    } catch (error) {
      console.error("Failed to fetch", error)
      //setAppLoading(false)
    }
  }

  useEffect(() => {
    loadAppConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchApps()
  }, [])

  const fetchAppConfigs = async () => {
    try {
      const data = await get("/admin/appConfig")
      setAppConfigs(data)
    } catch (error) {
      console.error("Failed to fetch app configs", error)
    }
  }

  const loadAppConfigs = async () => {
    setLoading(true)
    await fetchAppConfigs()
    setLoading(false)
  }

  const handleAddAppConfig = async (appConfig) => {
    setActionLoading(true)
    try {
      await post("/admin/appConfig", appConfig)
      fetchAppConfigs()
      setShowPopup(false)
      toast.success("AppConfig added successfully")
    } catch (error) {
      console.error("Failed to add app config", error)
      toast.error("Invalid AppKey")
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteAppConfig = async () => {
    setActionLoading(true)
    try {
      await remove(`/admin/appConfig?appId=${appIdToDelete}`)
      fetchAppConfigs()
      setAppIdToDelete(null)
      toast.success("AppConfig removed successfully")
    } catch (error) {
      console.error("Failed to delete app config", error)
      toast.error("Something went wrong")
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", marginTop: "20px", width: "100%" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div className="setting-note">
              Add API enabled Applications to be monitored
            </div>
            <button
              className="faxes-button"
              onClick={() => setShowPopup(true)}
              style={{ marginBottom: "5px" }}
            >
              Add Monitoring Application/s
            </button>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">App Name</th>
                  <th className="faxes-th">App ID</th>
                  <th className="faxes-th">Created By</th>
                  <th className="faxes-th">Created Date</th>
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {appConfigs.length > 0 ? (
                  appConfigs.map((config) => (
                    <tr
                      key={config.appId}
                      className={`fax-item${(hoveredRow === config.appId || activeRow === config.appId) ? " row-highlighted" : ""}`}
                      style={{ cursor: "unset" }}
                      onMouseEnter={() => {
                        setHoveredRow(config.appId)
                        if (activeRow !== config.appId) setActiveRow(null)
                      }}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="faxes-td">{config.appName}</td>
                      <td className="faxes-td">{config.appId}</td>
                      <td className="faxes-td">{config.createdBy}</td>
                      <td className="faxes-td">
                        {moment
                          .utc(config.createdDatetime * 1000)
                          .tz("America/New_York")
                          .format("MM/DD/YYYY hh:mm A")}
                      </td>
                      <td
                        className="faxes-td"
                        style={{ width: "100px", textAlign: "center" }}
                      >
                        <button
                          className="faxes-button delete-button"
                          onClick={() => { setAppIdToDelete(config.appId); setActiveRow(config.appId) }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showPopup && (
        <AppConfigForm
          onSave={handleAddAppConfig}
          onClose={() => setShowPopup(false)}
          apps={apps}
        />
      )}
      {appIdToDelete && (
        <DeleteConfirmationPopup
          onConfirm={handleDeleteAppConfig}
          onCancel={() => setAppIdToDelete(null)}
          actionLoading={actionLoading}
        />
      )}
    </div>
  )
}

const DeleteConfirmationPopup = ({ onConfirm, onCancel, actionLoading }) => (
  <div className="popup-overlay">
    <div className="popup">
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          <h3>Confirm Deletion</h3>
          <p>Are you sure you want to delete this application configuration?</p>
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)

const AppConfigForm = ({ onClose, onSave, apps }) => {
  const [appId, setAppId] = useState("")
  const [appSecret, setAppSecret] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedApp, setSelectedApp] = useState({})

  const handleAppChange = async (event) => {
    const appId = event.target.value

    const onSelectApp = apps.find((app) => app.app_id === appId)

    if (onSelectApp.appConfigured === true) {
      toast.error(
        `AppId already exist. Please delete ${onSelectApp.app_id} first.`,
        {
          duration: 5000,
        }
      )
      setSelectedApp({})
    } else {
      setSelectedApp(onSelectApp)
      setAppId(appId)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!appId) {
      setErrorMessage("App ID is required.")
      return
    }

    if (!appSecret) {
      setErrorMessage("AppKey is required.")
      return
    }

    onSave({
      appId: selectedApp.app_id,
      appSecret,
      appName: selectedApp.app_name,
    })
  }

  return (
    <div className="popup-overlay">
      <div className="popup">
        <h3>Add Application Configuration</h3>
        <form className="popup-group" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="appId">
              App ID: <span className="mandatory">*</span>
            </label>

            <select
              id="appId"
              className="select-dropdown"
              onChange={handleAppChange}
              value={selectedApp?.app_id || ""}
            >
              <option value="" disabled>
                Select Application
              </option>
              {apps?.map((app) => (
                <option key={app.app_id} value={app.app_id}>
                  {app.app_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="appSecret">
              AppKey: <span className="mandatory">*</span>
            </label>
            <input
              type="text"
              id="appSecret"
              value={appSecret}
              onChange={(e) => {
                setAppSecret(e.target.value)
                setErrorMessage("")
              }}
            />
            <div className="note">
              Note: Use AppID and AppKey to Add applications to be monitored.
            </div>
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="popup-buttons">
            <button
              className="popup-button cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="popup-button save" type="submit">
              Add Application
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MonitoringApplicationConfig
